import React, { useEffect, useRef, useState, useCallback } from 'react';

// ---------------------------------------------------------------------------
// TailorWiz Executive Bio — the approved navy one-page layout.
// Faithful port of the design handoff's template/executive-bio.html.
// High fidelity: colors, type, sizes, spacing, borders and shadows are final.
// Page = 1275x1650 CSS px (8.5x11in at 150dpi). Footer bar is ALWAYS 39px.
// ---------------------------------------------------------------------------

export interface ExecutiveBioData {
  name: string;
  title: string;
  contact: { location: string; phone: string; email: string; linkedin?: string };
  photoUrl?: string;
  summary: Array<{ html: string }>;
  hasStats?: boolean;
  stats?: Array<{ value: string; label: string }>;
  career: Array<{ html: string }>;
  closing?: string;
}

/** True when a stage-8 result (or stored content) is the structured navy bio. */
export function isStructuredBio(v: unknown): v is ExecutiveBioData {
  if (!v) return false;
  let o: any = v;
  if (typeof o === 'string') {
    try { o = JSON.parse(o); } catch { return false; }
  }
  return !!(o && typeof o === 'object' && o.name && Array.isArray(o.summary) && Array.isArray(o.career));
}

export function parseStructuredBio(v: unknown): ExecutiveBioData | null {
  if (!isStructuredBio(v)) return null;
  return typeof v === 'string' ? JSON.parse(v) : (v as ExecutiveBioData);
}

/** Only <b> and <b class="hl"> survive; everything else is escaped or stripped. */
export function sanitizeBioHtml(html: string): string {
  const escaped = String(html ?? '')
    .replace(/&(?!(amp|lt|gt|quot|#\d+|#x[0-9a-fA-F]+);)/g, '&amp;')
    .replace(/<(?!\/?b(\s+class="hl")?\s*>)/gi, '&lt;');
  // Normalize any em/en dashes that slipped through generation.
  return escaped.replace(/(\d)\s*[–—]\s*(\d)/g, '$1 to $2').replace(/\s*[–—]\s*/g, ', ');
}

const CSS = `
  .twbio-page { position: relative; width: 1275px; height: 1650px; overflow: hidden; background: #ffffff; display: flex; flex-direction: column; font-family: 'Source Serif 4', 'Palatino Linotype', Palatino, serif; color: #262626; }
  .twbio-page * { box-sizing: border-box; }
  .twbio-hdr { flex: none; background: #244061; padding: 34px 70px 30px; display: flex; align-items: flex-end; justify-content: space-between; gap: 30px; }
  .twbio-hdr-name { font-size: 44px; font-weight: 700; letter-spacing: 0.01em; color: #ffffff; line-height: 1; }
  .twbio-hdr-title { font-size: 21px; font-style: italic; color: rgba(255,255,255,0.85); margin-top: 6px; }
  .twbio-hdr-contact { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; font-size: 16.5px; color: rgba(255,255,255,0.88); line-height: 1.5; text-align: right; }
  .twbio-body { flex: 1; min-height: 0; padding: 32px 70px 60px; display: flex; flex-direction: column; gap: 18px; }
  .twbio-section { display: flex; flex-direction: column; gap: 13px; }
  .twbio-section + .twbio-section { margin-top: 12px; }
  .twbio-sec-head { display: flex; align-items: stretch; padding: 6px 0; }
  .twbio-sec-label { flex: none; background: #eef2f7; border: 1px solid #244061; padding: 8px 22px 8px 16px; font-size: 24px; font-weight: 700; letter-spacing: 0.02em; color: #244061; white-space: nowrap; }
  .twbio-sec-rule { flex: 1; align-self: center; height: 2px; background: linear-gradient(to right, #244061, rgba(36,64,97,0)); }
  .twbio-prose { display: block; font-size: var(--twbio-font, 19px); line-height: 1.52; text-wrap: pretty; }
  .twbio-prose p { margin: 0 0 var(--twbio-gap, 18px); }
  .twbio-prose p:last-child { margin-bottom: 0; }
  .twbio-prose b { font-weight: 700; }
  .twbio-prose b.hl { color: #244061; }
  .twbio-photo { float: right; padding: 8px; margin: 2px 0 10px 32px; background: #ffffff; border: 3px solid #14183a; box-shadow: 0 10px 26px rgba(20,26,48,0.25); }
  .twbio-photo img { display: block; width: 240px; height: 246px; object-fit: cover; object-position: 50% 20%; }
  .twbio-numbers { float: right; width: 350px; margin: 6px 0 16px 28px; border: 2px solid #244061; background: #ffffff; box-shadow: 0 0 22px 4px rgba(46,98,196,0.45); display: flex; flex-direction: column; overflow: hidden; }
  .twbio-numbers-head { background: #244061; padding: 9px 18px; display: flex; align-items: center; justify-content: center; gap: 10px; }
  .twbio-numbers-head span { width: 22px; height: 2px; background: rgba(255,255,255,0.5); }
  .twbio-numbers-head div { font-size: 16px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #ffffff; white-space: nowrap; }
  .twbio-numbers-grid { padding: 8px 20px 6px; display: grid; grid-template-columns: 1fr 1fr; gap: 0 20px; }
  .twbio-stat { display: flex; flex-direction: column; gap: 2px; padding: 8px 0; border-bottom: 1px solid #e3e8ee; }
  .twbio-stat:nth-last-child(-n+2) { border-bottom: none; }
  .twbio-stat-value { font-size: 27px; font-weight: 700; color: #244061; line-height: 1; }
  .twbio-stat-label { font-size: 13.5px; color: #595959; line-height: 1.25; }
  .twbio-closing { clear: both; }
  .twbio-footer { position: absolute; left: 0; right: 0; bottom: 0; height: 39px; background: #244061; }
`;

const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;0,8..60,700;1,8..60,400&display=swap';

function ensureFontLink() {
  if (!document.querySelector(`link[href="${FONT_HREF}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }
}

interface ExecutiveBioNavyProps {
  data: ExecutiveBioData;
  /** Overrides data.photoUrl (e.g. a freshly uploaded headshot as a data URI). */
  photoUrl?: string;
  /** Called after the fit check settles. ok=false means the copy is too long even at minimum size. */
  onFitResult?: (r: { ok: boolean; fontPx: number; gapPx: number }) => void;
}

// Fit steps per the handoff: 19px/18px -> 18.5px/14px -> 18px/14px, then fail.
const FIT_STEPS: Array<{ font: number; gap: number }> = [
  { font: 19, gap: 18 },
  { font: 18.5, gap: 14 },
  { font: 18, gap: 14 },
];
const FOOTER_TOP = 1650 - 39; // 1611
const FIT_MARGIN = 20;

export const ExecutiveBioNavy: React.FC<ExecutiveBioNavyProps> = ({ data, photoUrl, onFitResult }) => {
  const pageRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0);
  const [fitFailed, setFitFailed] = useState(false);

  useEffect(() => { ensureFontLink(); }, []);

  const measure = useCallback(async () => {
    const page = pageRef.current;
    if (!page) return;
    try { await (document as any).fonts?.ready; } catch { /* measure anyway */ }
    const paragraphs = page.querySelectorAll('.twbio-prose p');
    if (!paragraphs.length) return;
    const last = paragraphs[paragraphs.length - 1] as HTMLElement;
    const pageTop = page.getBoundingClientRect().top;
    const scale = page.getBoundingClientRect().height / 1650; // account for any CSS transform scaling
    const bottom = (last.getBoundingClientRect().bottom - pageTop) / (scale || 1);
    const overflows = bottom > FOOTER_TOP - FIT_MARGIN;
    if (overflows && step < FIT_STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      const failed = overflows;
      setFitFailed(failed);
      onFitResult?.({ ok: !failed, fontPx: FIT_STEPS[step].font, gapPx: FIT_STEPS[step].gap });
    }
  }, [step, onFitResult]);

  useEffect(() => { void measure(); }, [measure, data, photoUrl]);

  const photo = photoUrl || data.photoUrl;
  const stats = (data.hasStats ?? (data.stats?.length ?? 0) > 0) ? (data.stats ?? []) : [];
  // Standing rule: always an even number of stats — drop the orphan if one slips through.
  const evenStats = stats.length % 2 === 1 ? stats.slice(0, stats.length - 1) : stats;
  const { font, gap } = FIT_STEPS[step];

  return (
    <>
      <style>{CSS}</style>
      <div
        ref={pageRef}
        className="twbio-page"
        style={{ ['--twbio-font' as any]: `${font}px`, ['--twbio-gap' as any]: `${gap}px` }}
        data-fit-failed={fitFailed ? 'true' : undefined}
      >
        <div className="twbio-hdr">
          <div>
            <div className="twbio-hdr-name">{data.name}</div>
            <div className="twbio-hdr-title">{data.title}</div>
          </div>
          <div className="twbio-hdr-contact">
            <div>{data.contact.location} &nbsp;&middot;&nbsp; {data.contact.phone}</div>
            <div>{data.contact.email}</div>
            {data.contact.linkedin ? <div>{data.contact.linkedin}</div> : null}
          </div>
        </div>

        <div className="twbio-body">
          <div className="twbio-section">
            <div className="twbio-sec-head"><div className="twbio-sec-label">Executive Summary</div><div className="twbio-sec-rule"></div></div>
            <div className="twbio-prose">
              {photo ? (
                <div className="twbio-photo"><img src={photo} alt={data.name} crossOrigin="anonymous" /></div>
              ) : null}
              {data.summary.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: sanitizeBioHtml(p.html) }} />
              ))}
            </div>
          </div>

          <div className="twbio-section">
            <div className="twbio-sec-head"><div className="twbio-sec-label">Career Progression &amp; Highlights</div><div className="twbio-sec-rule"></div></div>
            <div className="twbio-prose">
              {evenStats.length > 0 && (
                <div className="twbio-numbers">
                  <div className="twbio-numbers-head"><span></span><div>By the Numbers</div><span></span></div>
                  <div className="twbio-numbers-grid">
                    {evenStats.map((s, i) => (
                      <div className="twbio-stat" key={i}>
                        <div className="twbio-stat-value">{s.value}</div>
                        <div className="twbio-stat-label">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data.career.map((p, i) => (
                <p key={i} dangerouslySetInnerHTML={{ __html: sanitizeBioHtml(p.html) }} />
              ))}
              {data.closing ? (
                <p className="twbio-closing" dangerouslySetInnerHTML={{ __html: sanitizeBioHtml(data.closing) }} />
              ) : null}
            </div>
          </div>
        </div>

        <div className="twbio-footer"></div>
      </div>
    </>
  );
};

/**
 * Export the rendered bio page to a one-page US Letter PDF.
 * Image-based export at 2x (2550x3300), matching the approved reference exports.
 * File name: plain words only, no dashes or parentheses (they broke downloads).
 */
export async function exportExecutiveBioPdf(pageEl: HTMLElement, fullName: string): Promise<void> {
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);
  try { await (document as any).fonts?.ready; } catch { /* proceed */ }
  const canvas = await html2canvas(pageEl, {
    scale: 2, width: 1275, height: 1650, backgroundColor: '#ffffff', useCORS: true, logging: false,
  });
  const img = canvas.toDataURL('image/jpeg', 0.95);
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'in', format: 'letter' });
  pdf.addImage(img, 'JPEG', 0, 0, 8.5, 11);
  const cleanName = fullName
    .replace(/,.*$/, '')                 // drop credentials after the comma
    .replace(/[^A-Za-z ]+/g, ' ')        // plain words only
    .replace(/\s+/g, ' ').trim();
  pdf.save(`${cleanName} Executive Bio.pdf`);
}
